import _ from 'lodash'
import { EntityState } from '@reduxjs/toolkit'

import { ExternalServiceType } from '@/constants/external-services'

import { CommonState, Model } from '@/core/classes/Model'
import { SearchRequestOptions, SearchStaffsAvailabilityRequestParams } from '@/core/types/request'
import { ContactInfo, KeycloakUserType, UserInfo } from '@/core/types/shared'

import { MainSteps } from '@/utils/onboarding/enum'
import { SubscriptionType } from '@/utils/subscription/types'

import { Consultation } from './consultation.model'
import { Establishment } from './establishment.model'
import { Room } from './room.model'
import { WaitingRoom } from './waiting-room.model'
import { Account } from './account.model'
import { Slot, WorkingHours } from './schedule.model'

export type ExternalAccountProvider = ExternalServiceType

export type ExternalAccount = {
  id: string
  email?: string
  username?: string
  entityId?: string
}

/**
 * Per-establishment external accounts structure
 * Key is establishmentId (or "legacy" for migrated data)
 */
export type EstablishmentExternalAccounts = Record<string, ExternalAccount>

/**
 * External accounts mapped by service provider, then by establishment
 * Structure: { serviceName: { establishmentId: ExternalAccount } }
 * Legacy format: { serviceName: { id: string } }
 */
export type PatientExternalAccounts = Partial<Record<ExternalAccountProvider, EstablishmentExternalAccounts | ExternalAccount>>

/**
 * Helper to get external ID with per-establishment and legacy fallback
 */
export function getExternalId(
  externalAccounts: PatientExternalAccounts | undefined,
  service: ExternalAccountProvider,
  establishmentId?: string
): string | undefined {
  const accounts = externalAccounts?.[service]
  if (!accounts) return undefined

  // Check if legacy format (has direct 'id' property at root)
  if ('id' in accounts && typeof accounts.id === 'string') {
    return accounts.id
  }

  // Per-establishment format
  const perEstAccounts = accounts as EstablishmentExternalAccounts
  if (establishmentId && perEstAccounts[establishmentId]?.id) {
    return perEstAccounts[establishmentId].id
  }

  // Fallback to legacy key
  return perEstAccounts['legacy']?.id
}

export type Calendso = {
  email?: string
  password?: string
  username?: string
  dateFrom?: string
  dateTo?: string
  end?: string
  eventTypeId?: number
  guests?: []
  language?: string
  name?: string
  notes?: string
  start?: string
  timeZone?: string
  types?: CalendsoEventType[]
  calendsoPassword?: string
}

export type CalendsoEventType = {
  id: number
  eventTypeName: string
  duree: number
  slug: string
}

export type Availability = {
  busy: Slot[]
  timeZone: string
  workingHours: WorkingHours[]
}

type TimeSlot = {
  consultation?: string
  dayOfWeek: 'monday' | 'tuesday' | 'wednesday' | 'thursday' | 'friday' | 'saturday' | 'sunday'
  startTime: string
  endTime: string
}

export enum StaffsPrivacy {
  Invisble = 'Invisble',
  Establishment = 'Establishment',
  Public = 'Public',
}

export enum Civility {
  DR = 'DR',
  PR = 'PR',
  MR = 'MR',
  MME = 'MME',
}

export type OnboardingProgress = Record<MainSteps, Record<string, boolean>>

export type Staff = Account & {
  consultations: Consultation[] | string[]
  cps?: string
  ecgFolder?: string
  etablissements: string[] | Establishment[]
  isFirstConnection?: boolean
  isEmailVerified?: boolean
  isPhoneVerified?: boolean
  netsoinsId?: string
  photo?: string
  speciality?: string
  status: 'MEDECIN' | 'PDS'
  street?: string
  stripe: string
  stripeActivated?: boolean
  showPayment?: boolean
  signature?: {
    data: string
    contentType: string
  }
  slots?: TimeSlot[]
  types?: any
  addedBy: string
  displayChat?: boolean
  contacts: { contact: string; requestBy: string; status: string }[]
  pluginId: string
  emergencyRoom?: Room | string
  conclusionSentences?: string[]
  externalAccounts: Partial<Record<ExternalAccountProvider, ExternalAccount>>
  activeWaitingRooms: string[]
  favoriteWaitingRooms: string[]
  baudrate?: number
  subscription: SubscriptionType
  privacy?: StaffsPrivacy
  onboardingProcess?: OnboardingProgress
  managePatientPreferences: {
    insEnabled?: boolean
  }
  /** Locked simplified reception interface, set by an admin (flag `simplified-reception-mode`). */
  simplifiedReceptionMode?: boolean
  civility?: Civility
  title?: string
  conflictOfInterest?: string
}

export type StaffAvailability = {
  id: string
  availability: Availability
}

export type CreateStaffDto = {
  userInfo: UserInfo
  type: KeycloakUserType
  contactInfo?: ContactInfo
  status: 'MEDECIN' | 'PDS'
  cps?: string
  email: string
  speciality?: string
}

export type UpdateStaffDto = Partial<CreateStaffDto>

export type ContactDto = {
  contact: string
  requestBy: string
  ok?: boolean
}

export type PluginTest = {
  name: string
  status: boolean
}

export type StaffState = CommonState & {
  availabilitiesByStaff: EntityState<StaffAvailability, StaffAvailability['id']>
  calendsoEventTypesByStaff: EntityState<
    {
      id: string
      events: CalendsoEventType[]
    },
    string
  >
  staffs: EntityState<Staff, Staff['_id']>
}

class StaffModel extends Model {
  static instance: StaffModel

  static getInstance() {
    if (!StaffModel.instance) {
      StaffModel.instance = new StaffModel('staff')
    }
    return StaffModel.instance
  }

  getInitialState(): StaffState {
    return _.merge({}, this.commonState, {
      availabilitiesByStaff: {
        entities: {},
        ids: [],
      },
      calendsoEventTypesByStaff: {
        entities: {},
        ids: [],
      },
      staffs: {
        entities: {},
        ids: [],
      },
    })
  }

  getAccountLink = async (accountLinkParams: { return_url?: string; refresh_url?: string } = {}) => {
    return await this.httpClient.request<string>('/charge/connect', 'GET', accountLinkParams)
  }

  getStaff = async (staffId: string): Promise<Staff> => {
    return await this.httpClient.request<Staff>(`/staffs/${staffId}`, 'GET')
  }

  getStripeAccount = async (): Promise<{ staff: Staff }> => {
    return await this.httpClient.request<{ staff: Staff }>(`/stripe/check-account-connected`, 'GET')
  }

  // TODO: add type for query
  getStaffs = async (query: Record<string, any>, options?: SearchRequestOptions): Promise<[Staff[], number]> => {
    const defaultOptions = {
      limit: 5,
      order: 'ASC',
      skip: 0,
      orderBy: '',
    }
    return await this.httpClient.request<[Staff[], number]>('/staffs/query', 'POST', { query, options: _.merge({}, defaultOptions, options) })
  }

  getStaffAvailability = async (username: string, dateFrom: string, dateTo: string, staffId: string): Promise<Availability> => {
    return await this.httpClient.request<Availability>(`/calendso-p/staff/availability`, 'GET', { username, dateFrom, dateTo, staffId })
  }

  searchStaffsAvailability = async ({ filters }: SearchStaffsAvailabilityRequestParams): Promise<StaffAvailability[]> => {
    return await this.httpClient.request<StaffAvailability[]>(`/calendso-p/staffs/availability`, 'POST', { filters })
  }

  searchStaffsEvents = async (list: { staffId: string }[]): Promise<{ eventTypes: CalendsoEventType[]; id: string }[]> => {
    return await this.httpClient.request<{ eventTypes: CalendsoEventType[]; id: string }[]>(`/calendso-p/staffs/events`, 'POST', { list })
  }

  handleContact = async (contact: Partial<ContactDto>): Promise<Staff> => {
    return await this.httpClient.request<Staff>('/staffs/contact', 'POST', { ...contact })
  }

  createDirectMsg = async (email: string): Promise<any> => {
    return await this.httpClient.request(`/rocketchat/direct`, 'POST', { email })
  }

  createArchiveRoom = async (roomId: string): Promise<any> => {
    return await this.httpClient.request(`/rocketchat/archive-room`, 'POST', { roomId })
  }

  getNotificationsChat = async (email: string): Promise<Number> => {
    return await this.httpClient.request(`/rocketchat/notification`, 'GET', { email })
  }

  addUserToRoom = async (consultationId: string, email: string) => {
    return await this.httpClient.request(`/rocketchat/joint`, 'POST', { consultationId, email })
  }

  // TODO: add type for query
  addStaff = async (staff: CreateStaffDto): Promise<Staff> => {
    return await this.httpClient.request<Staff>('/staffs', 'POST', { ...staff })
  }

  createStripeAccount = async (staffId: string): Promise<Staff> => {
    return await this.httpClient.request<Staff>(`/staffs/${staffId}/stripe`, 'POST')
  }

  deleteStripeAccount = async (staffId: string) => {
    return await this.httpClient.request<Staff>(`/staffs/${staffId}/stripe`, 'DELETE')
  }

  testPlugin = async (): Promise<[PluginTest]> => {
    return await this.httpClient.request<[PluginTest]>('/plugin/pings', 'GET')
  }
}

export default StaffModel.getInstance()
